import React, { useState } from "react";
import { FaFilter, FaCalendarAlt, FaChevronDown, FaChevronUp, FaUserSecret, FaStar, FaFileExcel } from "react-icons/fa";

const selectClass =
  "w-full px-4 py-1.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all cursor-pointer appearance-none pr-8";

const labelClass =
  "flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300";

const SelectWrapper = ({ children }) => (
  <div className="relative">
    {children}
    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
      <FaChevronDown className="w-3 h-3" />
    </div>
  </div>
);

const ClientFilters = ({
  searchQuery,
  setSearchQuery,
  employeeFilter,
  setEmployeeFilter,
  employees,
  stepFilter,
  setStepFilter,
  paymentFilter,
  setPaymentFilter,
  serviceFilter,
  setServiceFilter,
  taxYearStart,
  setTaxYearStart,
  taxYearEnd,
  setTaxYearEnd,
  dueDateStart,
  setDueDateStart,
  dueDateEnd,
  setDueDateEnd,
  selectionMode,
  setSelectionMode,
  ratingFilter,
  setRatingFilter,
  onExportExcel,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card mb-4 border border-gray-100 dark:border-dark-700">
      <div className="px-5 py-4">

        {/* ── Main Filter Row ── */}
        <div className="flex flex-col lg:flex-row gap-4 items-end">

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 flex-grow w-full">

            {/* Employee Filter */}
            <div className="space-y-2">
              <label className={labelClass}>
                <FaFilter className="w-3.5 h-3.5 text-primary-500" /> Filter by Employee
              </label>
              <SelectWrapper>
                <select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All Employees</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </div>

            {/* Step Filter */}
            <div className="space-y-2">
              <label className={labelClass}>
                <FaFilter className="w-3.5 h-3.5 text-primary-500" /> Filter by Step
              </label>
              <SelectWrapper>
                <select
                  value={stepFilter}
                  onChange={(e) => setStepFilter(e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All Steps</option>
                  <optgroup label="Total Status">
                    <option value="total-in-progress">Total In-Progress</option>
                    <option value="total-completed">Total Completed</option>
                    <option value="total-pending">Total Pending</option>
                  </optgroup>
                  <optgroup label="Step 1 Status">
                    <option value="step1-in-progress">Step 1 In-Progress</option>
                    <option value="step1-completed">Step 1 Completed</option>
                    <option value="step1-pending">Step 1 Pending</option>
                  </optgroup>
                  <optgroup label="Step 2 Status">
                    <option value="step2-in-progress">Step 2 In-Progress</option>
                    <option value="step2-completed">Step 2 Completed</option>
                    <option value="step2-pending">Step 2 Pending</option>
                  </optgroup>
                  <optgroup label="Step 3 Status">
                    <option value="step3-in-progress">Step 3 In-Progress</option>
                    <option value="step3-completed">Step 3 Completed</option>
                    <option value="step3-pending">Step 3 Pending</option>
                  </optgroup>
                  <optgroup label="Step 4 Status">
                    <option value="step4-in-progress">Step 4 In-Progress</option>
                    <option value="step4-completed">Step 4 Completed</option>
                    <option value="step4-pending">Step 4 Pending</option>
                  </optgroup>
                </select>
              </SelectWrapper>
            </div>

            {/* Payment Filter */}
            <div className="space-y-2">
              <label className={labelClass}>
                <FaFilter className="w-3.5 h-3.5 text-primary-500" /> Payment Status
              </label>
              <SelectWrapper>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="wip-unpaid">WIP–Unpaid</option>
                  <option value="p-extension">P Extension</option>
                  <option value="up-extension">UP Extension</option>
                </select>
              </SelectWrapper>
            </div>

            {/* Service Filter */}
            <div className="space-y-2">
              <label className={labelClass}>
                <FaFilter className="w-3.5 h-3.5 text-primary-500" /> Services
              </label>
              <SelectWrapper>
                <select
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All Services</option>
                  <option value="usa">USA Company</option>
                  <option value="tax">Tax Filing</option>
                  <option value="bookkeeping">Bookkeeping</option>
                  <option value="cfo">CFO Advisory</option>
                  <option value="registeredAgent">Registered Agent</option>
                </select>
              </SelectWrapper>
            </div>

            {/* Rating Filter */}
            <div className="space-y-2">
              <label className={labelClass}>
                <FaStar className="w-3.5 h-3.5 text-yellow-500" /> Feedback Rating
              </label>
              <SelectWrapper>
                <select
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  className={selectClass}
                >
                  <option value="all">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                  <option value="none">No Rating</option>
                </select>
              </SelectWrapper>
            </div>

          </div>{/* end grid */}

          {/* Date Filters Toggle & Export Excel */}
          <div className="flex items-center gap-2 lg:mb-0.5">
            {onExportExcel && (
              <button
                type="button"
                onClick={onExportExcel}
                className="flex items-center justify-center gap-2 px-3.5 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm transition-all whitespace-nowrap"
                title="Download all client service due dates & details in Excel"
              >
                <FaFileExcel className="w-3.5 h-3.5" />
                Export Excel
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-all whitespace-nowrap"
            >
              {showAdvanced ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
              {showAdvanced ? "Hide Date Filters" : "Date Filters"}
            </button>
          </div>

        </div>{/* end flex row */}

        {/* ── Advanced Date Filters ── */}
        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="space-y-2">
              <label className={labelClass}>
                <FaCalendarAlt className="w-3.5 h-3.5 text-primary-500" /> Tax Year End (From)
              </label>
              <input
                type="date"
                value={taxYearStart}
                onChange={(e) => setTaxYearStart(e.target.value)}
                className="w-full px-4 py-1.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
            </div>

            <div className="space-y-2">
              <label className={labelClass}>
                <FaCalendarAlt className="w-3.5 h-3.5 text-primary-500" /> Tax Year End (To)
              </label>
              <input
                type="date"
                value={taxYearEnd}
                onChange={(e) => setTaxYearEnd(e.target.value)}
                className="w-full px-4 py-1.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
            </div>

            <div className="space-y-2">
              <label className={labelClass}>
                <FaCalendarAlt className="w-3.5 h-3.5 text-primary-500" /> Due Date (From)
              </label>
              <input
                type="date"
                value={dueDateStart}
                onChange={(e) => setDueDateStart(e.target.value)}
                className="w-full px-4 py-1.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
            </div>

            <div className="space-y-2">
              <label className={labelClass}>
                <FaCalendarAlt className="w-3.5 h-3.5 text-primary-500" /> Due Date (To)
              </label>
              <input
                type="date"
                value={dueDateEnd}
                onChange={(e) => setDueDateEnd(e.target.value)}
                className="w-full px-4 py-1.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
            </div>
          </div>
        )}

      </div>{/* end px-5 py-4 */}
    </div>
  );
};

export default ClientFilters;